import { Banner } from 'slab'
import { conditionallyLocalizeDate, dateFromISOString } from 'utils'
import { type GetMemberOnboardingEnrollmentEventsQleMetadataQleType } from 'utils/web_api.schemas'

type QleType = GetMemberOnboardingEnrollmentEventsQleMetadataQleType

type BannerConfig = {
  title: string
  body: JSX.Element
}

const notBackdatedConfig = (submitByDate: string, standardCoverageStartDate: string): BannerConfig => {
  return {
    title: `Your new coverage starts ${standardCoverageStartDate}`,
    body: <p>Any plan or dependent changes will start on the month after you submit (e.g., {standardCoverageStartDate} if done by {submitByDate}).</p>,
  }
}

const notBackdatedMarriageConfig = (submitByDate: string, standardCoverageStartDate: string): BannerConfig => {
  return {
    title: `Your new coverage starts ${standardCoverageStartDate}`,
    body: <p>Any plan or dependent changes will start on the month after you submit (e.g., {standardCoverageStartDate} if done by {submitByDate}). (Congrats, btw!)</p>,
  }
}

const backdatedAddedChildConfig = (submitByDate: string, standardCoverageStartDate: string, backdatedCoverageStartDate: string): BannerConfig => {
  return {
    title: `Your new family member will be backdated to ${backdatedCoverageStartDate}`,
    body:
  <>
    <p>
      They'll be covered starting the month of their birth or adoption. (Congrats, btw!)
    </p>
    <p>
      If you want to make any other plan or dependent changes, everyone's new coverage will start on the month after you submit (e.g., {standardCoverageStartDate} if done by {submitByDate}).
    </p>
  </>,
  }
}

const backdatedCourtOrderConfig = (submitByDate: string, standardCoverageStartDate: string, backdatedCoverageStartDate: string): BannerConfig => {
  return {
    title: `Your new dependent will be backdated to ${backdatedCoverageStartDate}`,
    body:
  <>
    <p>
      They'll be covered starting the month of the court order.
    </p>
    <p>
      If you want to make any other plan or dependent changes, everyone's new coverage will start on the month after you submit (e.g., {standardCoverageStartDate} if done by {submitByDate}).
    </p>
  </>,
  }
}

function getBannerConfig (qleType: QleType, standardCoverageStartDate: string, backdatedCoverageStartDate: string, submitByDate: string) {
  switch (qleType) {
    case 'marriage':
      return notBackdatedMarriageConfig(submitByDate, standardCoverageStartDate)
    case 'birth_or_adoption':
      return backdatedAddedChildConfig(submitByDate, standardCoverageStartDate, backdatedCoverageStartDate)
    case 'court_ordered':
      return backdatedCourtOrderConfig(submitByDate, standardCoverageStartDate, backdatedCoverageStartDate)
    default:
      return notBackdatedConfig(submitByDate, standardCoverageStartDate)
  }
}

export const QleCoverageStartBanner = (
  { qleType, standardCoverageStartDate, backdatedCoverageStartDate, submitByDate }:
  { qleType: QleType, standardCoverageStartDate: string, backdatedCoverageStartDate: string, submitByDate: string }
) => {
  if (!submitByDate || !standardCoverageStartDate) return null

  const formattedSubmitByDate = conditionallyLocalizeDate(dateFromISOString(submitByDate))
  const formattedStandardCoverageStartDate = conditionallyLocalizeDate(dateFromISOString(standardCoverageStartDate))
  const formattedBackdatedCoverageStartDate = backdatedCoverageStartDate
    ? conditionallyLocalizeDate(dateFromISOString(backdatedCoverageStartDate))
    : ''
  const bannerConfig = getBannerConfig(qleType, formattedStandardCoverageStartDate, formattedBackdatedCoverageStartDate, formattedSubmitByDate)

  return (
    <Banner type='info' title={bannerConfig.title}>
      {bannerConfig.body}
    </Banner>
  )
}
